import { Link } from "react-router";
import { useTranslation } from "react-i18next";

import type { ProjectRef, Task, TaskStatus } from "@/api/queries";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { DueDate, TaskTime } from "./TaskCard";
import { TaskStatusBadge, TaskStatusSelect } from "./TaskStatus";

type TaskTableProps = {
  tasks: Task[];
  today: string;
  /** Assignee names; without it the assignee column is hidden. */
  names?: Map<string, string>;
  /** Projects of the tasks; with it a project column is shown. */
  projects?: Map<string, ProjectRef>;
  canMove: (task: Task) => boolean;
  onOpen?: (task: Task) => void;
  onMove: (task: Task, status: TaskStatus) => void;
};

export function TaskTable({ tasks, today, names, projects, canMove, onOpen, onMove }: TaskTableProps) {
  const { t } = useTranslation();
  return (
    <Table className="min-w-[720px]">
      <THead>
        <tr>
          <TH>{t("tasks.fields.task")}</TH>
          {projects && <TH>{t("tasks.fields.project")}</TH>}
          {names && <TH>{t("tasks.fields.assignee")}</TH>}
          <TH>{t("tasks.fields.dueDate")}</TH>
          <TH>{t("tasks.fields.tracked")}</TH>
          <TH className="w-44">{t("tasks.fields.status")}</TH>
        </tr>
      </THead>
      <tbody>
        {tasks.map((task) => {
          const project = projects?.get(task.project_id);
          const assignee = task.assignee_id ? names?.get(task.assignee_id) : undefined;
          return (
            <TR key={task.id}>
              <TD className="max-w-md">
                {onOpen ? (
                  <button
                    type="button"
                    onClick={() => onOpen(task)}
                    className={cn(
                      "cursor-pointer text-left font-medium hover:text-primary",
                      task.status === "done" && "text-muted line-through",
                    )}
                  >
                    {task.title}
                  </button>
                ) : (
                  <span className={cn("font-medium", task.status === "done" && "text-muted line-through")}>
                    {task.title}
                  </span>
                )}
                {task.description && <p className="line-clamp-1 text-xs text-muted">{task.description}</p>}
              </TD>
              {projects && (
                <TD>
                  {project && (
                    <Link
                      to={`/projects/${project.id}`}
                      className="inline-flex items-center gap-2 font-medium hover:text-primary"
                    >
                      <span
                        className="size-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: project.color }}
                      />
                      {project.name}
                    </Link>
                  )}
                </TD>
              )}
              {names && (
                <TD>
                  {assignee ? (
                    <span className="inline-flex items-center gap-2">
                      <UserAvatar name={assignee} className="size-6 text-[10px]" />
                      {assignee}
                    </span>
                  ) : (
                    <span className="text-muted">{t("tasks.unassigned")}</span>
                  )}
                </TD>
              )}
              <TD>
                <DueDate task={task} today={today} />
              </TD>
              <TD>
                <TaskTime task={task} />
              </TD>
              <TD>
                {canMove(task) ? (
                  <TaskStatusSelect
                    label={t("tasks.statusOf", { name: task.title })}
                    value={task.status}
                    onChange={(s) => onMove(task, s)}
                  />
                ) : (
                  <TaskStatusBadge status={task.status} />
                )}
              </TD>
            </TR>
          );
        })}
      </tbody>
    </Table>
  );
}
